import React, { useState } from 'react';
import { Desktop } from 'assets/preview/Desktop';
import { Mobile } from 'assets/preview/Mobile';
import os from 'assets/preview/OS.svg';
import { IGPDetail } from 'types/operation';
import GroupProlistings from 'pages/operation/GroupPracticeDetailPage/components/GroupProlistings';
import s from './s.module.less';

interface KlarityProfileTabProps {
    detail: IGPDetail;
    profileLink: string;
}

type SubTab = 'About' | 'Pro-Listings';

const KlarityProfileTab: React.FC<KlarityProfileTabProps> = ({ detail, profileLink }) => {
    // About describes the live HelloKlarity group profile, so it only exists once the
    // practice is listed. Pro-Listings stays reachable in every listing status.
    const isListed = detail.listingStatus === 'LISTED';
    const subTabs: SubTab[] = isListed ? ['About', 'Pro-Listings'] : ['Pro-Listings'];

    const [subTab, setSubTab] = useState<SubTab>(isListed ? 'About' : 'Pro-Listings');
    const [isMobile, setIsMobile] = useState(false);

    // Guard against a stale 'About' selection after the practice gets paused/unlisted.
    const activeSubTab: SubTab = isListed ? subTab : 'Pro-Listings';

    return (
        <div className={s.wrap}>
            <div className={s.leftPanel}>
                <div className={s.subTabs}>
                    {subTabs.map((tab) => (
                        <div
                            key={tab}
                            className={`${s.subTab} ${activeSubTab === tab ? s.subTabActive : ''}`}
                            onClick={() => setSubTab(tab)}
                        >
                            {tab}
                        </div>
                    ))}
                </div>

                {activeSubTab === 'About' && (
                    <div className={s.aboutSection}>
                        <div className={s.card}>
                            <div className={s.cardHeader}>
                                <h3>Basic Info</h3>
                            </div>
                            <div className={s.cardBody}>
                                <div className={s.field}>
                                    <span className={s.fieldLabel}>Name</span>
                                    <span className={s.fieldValue}>{detail.name}</span>
                                </div>
                                <div className={s.field}>
                                    <span className={s.fieldLabel}>Headlines</span>
                                    <span className={s.fieldValue}>{detail.headLine || '-'}</span>
                                </div>
                                <div className={s.field}>
                                    <span className={s.fieldLabel}>Specialties</span>
                                    <span className={s.fieldValue}>
                                        {detail.specialtyList?.length > 0 ? detail.specialtyList.join(', ') : '-'}
                                    </span>
                                </div>
                                <div className={s.field}>
                                    <span className={s.fieldLabel}>Profile photo</span>
                                    {detail.photo || (detail.photoList && detail.photoList.length > 0) ? (
                                        <img
                                            src={detail.photo || detail.photoList![0]}
                                            alt="Profile"
                                            className={s.avatar}
                                        />
                                    ) : (
                                        <div className={s.avatarPlaceholder} />
                                    )}
                                </div>
                                <div className={s.field}>
                                    <span className={s.fieldLabel}>Intro</span>
                                    <span className={s.fieldValue}>{detail.intro || '-'}</span>
                                </div>
                                <div className={s.fieldRow}>
                                    <div className={s.field}>
                                        <span className={s.fieldLabel}>Rating count</span>
                                        <span className={s.fieldValue}>{detail.ratingCount || 0}</span>
                                    </div>
                                    <div className={s.field}>
                                        <span className={s.fieldLabel}>Review count</span>
                                        <span className={s.fieldValue}>{detail.reviewCount || 0}</span>
                                    </div>
                                </div>
                                <div className={s.field}>
                                    <span className={s.fieldLabel}>Listed providers</span>
                                    <span className={s.fieldValue}>{detail.listedProviderCount}</span>
                                </div>
                                <div className={s.field}>
                                    <span className={s.fieldLabel}>Insurances</span>
                                    {detail.insurances?.length > 0 ? (
                                        <div className={s.insuranceList}>
                                            {Object.entries(
                                                detail.insurances.reduce<Record<string, { state: string; plans: string[] }[]>>(
                                                    (acc, ins) => {
                                                        if (!acc[ins.carrier]) acc[ins.carrier] = [];
                                                        acc[ins.carrier].push({ state: ins.state, plans: ins.plans });
                                                        return acc;
                                                    },
                                                    {},
                                                ),
                                            ).map(([carrier, items]) => (
                                                <div key={carrier} className={s.insuranceItem}>
                                                    <span className={s.insuranceCarrier}>{carrier}</span>
                                                    {items.map((item, idx) => (
                                                        <span key={idx} className={s.insuranceMeta}>
                                                            {item.state} · {item.plans.join(', ')}
                                                        </span>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className={s.fieldValue}>-</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeSubTab === 'Pro-Listings' && <GroupProlistings organizationId={detail.organizationId} />}
            </div>

            <div className={s.rightPanel}>
                <div className={s.modeWrap}>
                    <span onClick={() => setIsMobile(false)} className={isMobile ? '' : s.active}>
                        <Desktop />
                    </span>
                    <span onClick={() => setIsMobile(true)} className={isMobile ? s.active : ''}>
                        <Mobile />
                    </span>
                </div>
                <div className={isMobile ? `${s.iframeWrap} ${s.iframeWrapMobile}` : s.iframeWrap}>
                    <div className={s.topBar}>
                        <img src={os} />
                    </div>
                    {profileLink && isListed ? (
                        <iframe src={profileLink} title="Profile preview" />
                    ) : (
                        <div className={s.previewPlaceholder}>
                            {isListed
                                ? 'No profile link available'
                                : 'Profile preview is available once this practice is listed'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KlarityProfileTab;
